import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { TargetRangeController } from './TargetRangeController';
import { DiceRollController } from './DiceRollController';
import { ThirdPersonCameraController } from '../../../../CombatSystem/Modules/Camera/Classes/ThirdPersonCameraController';
import { PlayerMovementController } from '../../../../CombatSystem/Modules/Movement/Classes/PlayerMovementController';
import { AnimationController } from '../../../../CombatSystem/Modules/Animation/Classes/AnimationController';
import { CrosshairController } from '../../../../CombatSystem/Modules/Weapons/Classes/CrosshairController';
import { WeaponController } from '../../../../CombatSystem/Modules/Weapons/Classes/WeaponController';
import { PlayerCombatController } from '../../../../CombatSystem/Modules/Combat/Classes/PlayerCombatController';

type StationData = {
  name: string;
  position: Vector3;
  range: number;
  promptEl: HTMLElement | null;
  active: boolean;
  onInteract: () => void;
};

class LobbyHubController {
  game: any;
  stations: StationData[];
  targetRange: TargetRangeController | null;
  diceRoll: DiceRollController | null;
  promptEl: HTMLElement | null;
  _lastInteractCheck: number;
  _playerPos: Vector3;
  thirdPersonCamera: ThirdPersonCameraController | null;
  playerMovement: PlayerMovementController | null;
  animationController: AnimationController | null;
  crosshair: CrosshairController | null;
  weaponController: WeaponController | null;
  combatController: PlayerCombatController | null;

  constructor(game: any) {
    this.game = game;
    this.stations = [];
    this.targetRange = null;
    this.diceRoll = null;
    this.promptEl = null;
    this._lastInteractCheck = 0;
    this._playerPos = new Vector3(0, 0, 0);
    this.thirdPersonCamera = null;
    this.playerMovement = null;
    this.animationController = null;
    this.crosshair = null;
    this.weaponController = null;
    this.combatController = null;
  }

  async init() {
    this.createSpawnPoint();
    this.createTargetRangeStation();
    this.createDiceRollStation();
    this.createSocialArea();
    this.createSignage();
    this.createProximityPrompt();
    this.initCombatSystem();
  }

  initCombatSystem() {
    const game = this.game;
    if (!game.scene) return;

    this.thirdPersonCamera = new ThirdPersonCameraController(game);
    this.playerMovement = new PlayerMovementController(game);
    this.animationController = new AnimationController(game);
    this.crosshair = new CrosshairController(game);
    this.weaponController = new WeaponController(game);
    this.combatController = new PlayerCombatController(game);

    game.thirdPersonCameraController = this.thirdPersonCamera;
    game.playerMovementController = this.playerMovement;
    game.animationController = this.animationController;
    game.crosshairController = this.crosshair;
    game.weaponController = this.weaponController;
    game.playerCombatController = this.combatController;

    if (game.playerMesh) {
      this.thirdPersonCamera.init(game.scene, game.playerMesh);
      this.playerMovement.init(game.scene, game.playerMesh);
      this.animationController.init(game.scene);
      this.weaponController.init(game.scene, game.playerMesh);
    }

    this.crosshair.init();
    this.combatController.init();

    document.addEventListener('mousedown', (e) => {
      if (e.button === 0 && game.mapName !== 'lobby') {
        if (this.weaponController) this.weaponController.startFiring();
      }
    });
    document.addEventListener('mouseup', (e) => {
      if (e.button === 0) {
        if (this.weaponController) this.weaponController.stopFiring();
      }
    });
    document.addEventListener('contextmenu', (e) => {
      if (game.mapName !== 'lobby') {
        e.preventDefault();
        if (this.crosshair) this.crosshair.setAiming(true);
      }
    });
    document.addEventListener('mouseup', (e) => {
      if (e.button === 2) {
        if (this.crosshair) this.crosshair.setAiming(false);
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'q' || e.key === 'Q') {
        if (this.weaponController) this.weaponController.nextWeapon();
      }
    });
  }

  createSpawnPoint() {
    const game = this.game;
    if (!game.scene) return;

    const ring = MeshBuilder.CreateTorus('hubSpawnRing', {
      diameter: 2,
      thickness: 0.1,
      tessellation: 24
    }, game.scene);
    ring.position = new Vector3(0, 0.05, 0);
    ring.rotation.x = Math.PI / 2;
    const ringMat = new StandardMaterial('hubSpawnRingMat', game.scene);
    ringMat.emissiveColor = new Color3(0.3, 0.8, 1.0);
    ringMat.diffuseColor = new Color3(0.3, 0.8, 1.0);
    ringMat.specularColor = Color3.Black();
    ring.material = ringMat;

    const disc = MeshBuilder.CreateDisc('hubSpawnDisc', {
      radius: 0.8,
      tessellation: 16
    }, game.scene);
    disc.position = new Vector3(0, 0.02, 0);
    disc.rotation.x = Math.PI / 2;
    const discMat = new StandardMaterial('hubSpawnDiscMat', game.scene);
    discMat.emissiveColor = new Color3(0.2, 0.6, 0.9);
    discMat.diffuseColor = new Color3(0.2, 0.6, 0.9);
    discMat.alpha = 0.4;
    discMat.backFaceCulling = false;
    disc.material = discMat;
  }

  createTargetRangeStation() {
    const game = this.game;
    if (!game.scene) return;

    const platPos = new Vector3(20, 0, -20);
    const platform = MeshBuilder.CreateBox('targetRangePlatform', {
      width: 8, height: 0.5, depth: 6
    }, game.scene);
    platform.position = new Vector3(platPos.x, 0.25, platPos.z);
    const platMat = new StandardMaterial('targetRangePlatMat', game.scene);
    platMat.diffuseColor = new Color3(0.15, 0.15, 0.2);
    platMat.specularColor = new Color3(0.05, 0.05, 0.1);
    platMat.emissiveColor = new Color3(0.05, 0.05, 0.08);
    platform.material = platMat;

    const signPost = MeshBuilder.CreateBox('targetRangeSignPost', {
      width: 0.1, height: 2.0, depth: 0.1
    }, game.scene);
    signPost.position = new Vector3(platPos.x + 3.5, 1.0, platPos.z);
    const postMat = new StandardMaterial('targetRangePostMat', game.scene);
    postMat.diffuseColor = new Color3(0.3, 0.3, 0.3);
    signPost.material = postMat;

    const signBoard = MeshBuilder.CreateBox('targetRangeSign', {
      width: 1.6, height: 0.6, depth: 0.05
    }, game.scene);
    signBoard.position = new Vector3(platPos.x + 3.5, 2.0, platPos.z);
    const signMat = new StandardMaterial('targetRangeSignMat', game.scene);
    signMat.diffuseColor = new Color3(0.9, 0.2, 0.2);
    signMat.emissiveColor = new Color3(0.5, 0.1, 0.1);
    signMat.specularColor = Color3.Black();
    signBoard.material = signMat;

    const textBar = MeshBuilder.CreateBox('targetRangeTextBar', {
      width: 1.4, height: 0.3, depth: 0.02
    }, game.scene);
    textBar.position = new Vector3(platPos.x + 3.5, 2.0, platPos.z + 0.04);
    const textMat = new StandardMaterial('targetRangeTextMat', game.scene);
    textMat.diffuseColor = new Color3(1, 1, 1);
    textMat.emissiveColor = new Color3(0.8, 0.8, 0.8);
    textBar.material = textMat;

    this.targetRange = new TargetRangeController(game, platPos);

    this.stations.push({
      name: 'Target Range',
      position: platPos,
      range: 4.0,
      promptEl: null,
      active: false,
      onInteract: () => {
        if (this.targetRange && !this.targetRange.isActive) {
          this.targetRange.startGame();
        }
      }
    });
  }

  createDiceRollStation() {
    const game = this.game;
    if (!game.scene) return;

    const platPos = new Vector3(-20, 0, -20);
    const platform = MeshBuilder.CreateBox('diceRollPlatform', {
      width: 6, height: 0.5, depth: 6
    }, game.scene);
    platform.position = new Vector3(platPos.x, 0.25, platPos.z);
    const platMat = new StandardMaterial('diceRollPlatMat', game.scene);
    platMat.diffuseColor = new Color3(0.2, 0.15, 0.15);
    platMat.specularColor = new Color3(0.05, 0.03, 0.03);
    platMat.emissiveColor = new Color3(0.05, 0.03, 0.03);
    platform.material = platMat;

    const signPost = MeshBuilder.CreateBox('diceRollSignPost', {
      width: 0.1, height: 2.0, depth: 0.1
    }, game.scene);
    signPost.position = new Vector3(platPos.x + 2.5, 1.0, platPos.z);
    const postMat = new StandardMaterial('diceRollPostMat', game.scene);
    postMat.diffuseColor = new Color3(0.3, 0.3, 0.3);
    signPost.material = postMat;

    const signBoard = MeshBuilder.CreateBox('diceRollSign', {
      width: 1.6, height: 0.6, depth: 0.05
    }, game.scene);
    signBoard.position = new Vector3(platPos.x + 2.5, 2.0, platPos.z);
    const signMat = new StandardMaterial('diceRollSignMat', game.scene);
    signMat.diffuseColor = new Color3(0.2, 0.6, 0.2);
    signMat.emissiveColor = new Color3(0.1, 0.3, 0.1);
    signMat.specularColor = Color3.Black();
    signBoard.material = signMat;

    const diceCube = MeshBuilder.CreateBox('diceProp', {
      width: 0.4, height: 0.4, depth: 0.4
    }, game.scene);
    diceCube.position = new Vector3(platPos.x, 0.7, platPos.z);
    const diceMat = new StandardMaterial('dicePropMat', game.scene);
    diceMat.diffuseColor = new Color3(0.9, 0.9, 0.9);
    diceMat.specularColor = new Color3(0.2, 0.2, 0.2);
    diceCube.material = diceMat;

    this.diceRoll = new DiceRollController(game);

    this.stations.push({
      name: 'Dice Roll',
      position: platPos,
      range: 3.5,
      promptEl: null,
      active: false,
      onInteract: () => {
        if (this.diceRoll) {
          this.diceRoll.rollDice();
        }
      }
    });
  }

  createSocialArea() {
    const game = this.game;
    if (!game.scene) return;

    const socialCenter = new Vector3(0, 0, 20);

    const fireRing = MeshBuilder.CreateTorus('socialFireRing', {
      diameter: 1.5, thickness: 0.1, tessellation: 16
    }, game.scene);
    fireRing.position = new Vector3(socialCenter.x, 0.05, socialCenter.z);
    fireRing.rotation.x = Math.PI / 2;
    const ringMat = new StandardMaterial('socialFireRingMat', game.scene);
    ringMat.diffuseColor = new Color3(0.2, 0.15, 0.1);
    ringMat.specularColor = new Color3(0.05, 0.03, 0.02);
    fireRing.material = ringMat;

    const fireGlow = MeshBuilder.CreateSphere('socialFireGlow', {
      diameter: 0.8, segments: 8
    }, game.scene);
    fireGlow.position = new Vector3(socialCenter.x, 0.2, socialCenter.z);
    const glowMat = new StandardMaterial('socialFireGlowMat', game.scene);
    glowMat.emissiveColor = new Color3(1.0, 0.5, 0.1);
    glowMat.diffuseColor = new Color3(1.0, 0.6, 0.2);
    glowMat.alpha = 0.6;
    glowMat.backFaceCulling = false;
    fireGlow.material = glowMat;

    const benchMat = new StandardMaterial('socialBenchMat', game.scene);
    benchMat.diffuseColor = new Color3(0.3, 0.2, 0.1);
    benchMat.specularColor = new Color3(0.05, 0.03, 0.02);
    const benchPositions = [
      { x: -2.5, z: 0 },
      { x: 2.5, z: 0 },
      { x: 0, z: -2.5 },
      { x: 0, z: 2.5 },
    ];
    benchPositions.forEach((bp, idx) => {
      const seat = MeshBuilder.CreateBox('socialBench_' + idx, {
        width: 1.2, height: 0.15, depth: 0.5
      }, game.scene);
      seat.position = new Vector3(socialCenter.x + bp.x, 0.3, socialCenter.z + bp.z);
      seat.material = benchMat;

      const legMat = new StandardMaterial('socialBenchLegMat_' + idx, game.scene);
      legMat.diffuseColor = new Color3(0.15, 0.12, 0.08);
      for (let lx = -1; lx <= 1; lx += 2) {
        for (let lz = -1; lz <= 1; lz += 2) {
          const leg = MeshBuilder.CreateBox('socialBenchLeg_' + idx + '_' + lx + '_' + lz, {
            width: 0.08, height: 0.25, depth: 0.08
          }, game.scene);
          leg.position = new Vector3(socialCenter.x + bp.x + lx * 0.5, 0.125, socialCenter.z + bp.z + lz * 0.2);
          leg.material = legMat;
        }
      }
    });
  }

  createSignage() {
    const game = this.game;
    if (!game.scene) return;

    const welcomePost = MeshBuilder.CreateBox('welcomeSignPost', {
      width: 0.1, height: 3.0, depth: 0.1
    }, game.scene);
    welcomePost.position = new Vector3(5, 1.5, 0);
    const postMat = new StandardMaterial('welcomePostMat', game.scene);
    postMat.diffuseColor = new Color3(0.2, 0.2, 0.25);
    welcomePost.material = postMat;

    const welcomeBoard = MeshBuilder.CreateBox('welcomeSign', {
      width: 2.0, height: 1.0, depth: 0.05
    }, game.scene);
    welcomeBoard.position = new Vector3(5, 3.0, 0);
    const boardMat = new StandardMaterial('welcomeBoardMat', game.scene);
    boardMat.diffuseColor = new Color3(0.15, 0.2, 0.3);
    boardMat.emissiveColor = new Color3(0.05, 0.08, 0.15);
    boardMat.specularColor = Color3.Black();
    welcomeBoard.material = boardMat;

    const neonStrip = MeshBuilder.CreateBox('welcomeNeon', {
      width: 2.1, height: 0.03, depth: 0.06
    }, game.scene);
    neonStrip.position = new Vector3(5, 2.5, 0.05);
    const neonMat = new StandardMaterial('welcomeNeonMat', game.scene);
    neonMat.emissiveColor = new Color3(0.3, 0.8, 1.0);
    neonMat.diffuseColor = new Color3(0.3, 0.8, 1.0);
    neonStrip.material = neonMat;

    const neonStrip2 = MeshBuilder.CreateBox('welcomeNeon2', {
      width: 2.1, height: 0.03, depth: 0.06
    }, game.scene);
    neonStrip2.position = new Vector3(5, 3.5, 0.05);
    neonStrip2.material = neonMat;
  }

  createProximityPrompt() {
    let promptEl = document.getElementById('hub-interact-prompt');
    if (!promptEl) {
      promptEl = document.createElement('div');
      promptEl.id = 'hub-interact-prompt';
      promptEl.className = 'hub-interact-prompt';
      promptEl.style.display = 'none';
      document.body.appendChild(promptEl);
    }
    this.promptEl = promptEl;
  }

  update(delta: number) {
    const game = this.game;
    if (!game.playerMesh || !game.scene) return;
    if (game.mapName !== 'lobby') return;

    this._lastInteractCheck += delta;
    if (this._lastInteractCheck < 0.2) return;
    this._lastInteractCheck = 0;

    const pp = game.playerMesh.position;
    this._playerPos.set(pp.x, pp.y, pp.z);

    let nearestStation: StationData | null = null;
    let nearestDist = Infinity;

    for (const station of this.stations) {
      const dx = this._playerPos.x - station.position.x;
      const dz = this._playerPos.z - station.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist < station.range && dist < nearestDist) {
        nearestStation = station;
        nearestDist = dist;
      }
    }

    if (nearestStation && this.promptEl) {
      this.promptEl.textContent = 'Press E to interact: ' + nearestStation.name;
      this.promptEl.style.display = 'block';
      this.promptEl.style.bottom = '80px';
      this.promptEl.style.left = '50%';
      this.promptEl.style.transform = 'translateX(-50%)';
    } else if (this.promptEl) {
      this.promptEl.style.display = 'none';
    }

    if (nearestStation && game.input && game.input.e) {
      if (!nearestStation.active) {
        nearestStation.active = true;
        nearestStation.onInteract();
      }
    } else if (nearestStation) {
      nearestStation.active = false;
    }

    if (this.targetRange && this.targetRange.isActive) {
      this.targetRange.update(delta);
    }

    if (this.thirdPersonCamera) this.thirdPersonCamera.update(delta);
    if (this.playerMovement) this.playerMovement.update(delta);
    if (this.animationController) {
      if (this.playerMovement && this.playerMovement.isMoving()) {
        if (this.playerMovement.isSprinting()) {
          this.animationController.setState('run');
        } else {
          this.animationController.setState('walk');
        }
      } else if (this.playerMovement && !this.playerMovement.isGrounded()) {
        this.animationController.setState('jump');
      } else {
        this.animationController.setState('idle');
      }
      this.animationController.update(delta);
    }
    if (this.crosshair) this.crosshair.update(delta);
    if (this.weaponController) this.weaponController.update(delta);
    if (this.combatController) this.combatController.update(delta);
  }

  dispose() {
    if (this.promptEl && this.promptEl.parentNode) {
      this.promptEl.parentNode.removeChild(this.promptEl);
    }
    this.stations = [];
    if (this.targetRange) {
      this.targetRange.dispose();
      this.targetRange = null;
    }
    this.diceRoll = null;
    if (this.thirdPersonCamera) {
      this.thirdPersonCamera.dispose();
      this.thirdPersonCamera = null;
    }
    if (this.playerMovement) {
      this.playerMovement.dispose();
      this.playerMovement = null;
    }
    if (this.animationController) {
      this.animationController.dispose();
      this.animationController = null;
    }
    if (this.crosshair) {
      this.crosshair.dispose();
      this.crosshair = null;
    }
    if (this.weaponController) {
      this.weaponController.dispose();
      this.weaponController = null;
    }
    if (this.combatController) {
      this.combatController.dispose();
      this.combatController = null;
    }
  }
}

export { LobbyHubController };
